import React, { useContext, useMemo } from 'react';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { StyledNode } from 'craft/components/Node';
import { CollectionContext } from 'craft/contexts';
import { usePropsWithCollection, useResponsiveCss } from 'craft/hooks';
import { unpackShorthandStyles } from 'craft/utils';

import { CollectionNode } from '../CollectionNode';

// TODO this might get complex when we add list / list items
const styleguideMap = {
  Typography: 'typography',
  Link: 'link',
  ListItem: 'typography',
  List: 'list',
};

// TODO better typing for elements
const getStyleguideClass = (element: CraftElement) => {
  // @ts-ignore
  const styleguideType = styleguideMap[element.type];

  if (!styleguideType) {
    return toCssSelector('typography', {
      variant: { variant: 'base' },
      output: 'html',
    });
  }

  const { variant, variation } = element.data;

  const typographyVariant = {
    variant: variation || variant || 'base',
  };

  return toCssSelector(styleguideType, {
    variant: typographyVariant,
    output: 'html',
  });
};

/**
 * Similar to craft/components/Node in which the Node's data.css and event handlers are attached here
 * Only difference is that this is specific to RTE elements (ie: Typography/List/ListItem)
 *
 * This is because RTE elements cannot depend on Craft's state (useNode()/useEditor() hook) to retrieve state values
 * Instead, the latest Node values are passed in the `element` prop
 */

// TODO do we already have interface for craft elements?
interface CraftElement {
  id: string;
  type: string;
  data: {
    variant?: string;
    variation?: string;
    css?: any;
  };
}

interface Props {
  is: string;
  attributes: any;
  element: CraftElement;
  children: any;
  style?: any;
}

export const RTENode = StyledNode(({ is, attributes, element, children, style }: Props) => {
  const { id } = element;
  const { responsiveCss } = useResponsiveCss([id]);
  const comp = is || 'div';

  const { id: collectionItemId } = useContext(CollectionContext);
  const propsWithSource = usePropsWithCollection(id);
  const isMissingContentCollectionItem = Object.values(propsWithSource).some(
    (entry) => entry === null,
  );

  const className = `${getStyleguideClass(element)} ${
    isMissingContentCollectionItem ? 'collection-item--hidden' : ''
  }
    ${propsWithSource.Text ? 'collection-item' : ''}
  `.trim();

  const sanitizedChildren = useMemo(() => {
    if (propsWithSource.Text) {
      const textChild =
        children.find(
          // @ts-expect-error
          (child) => !!child.props?.text?.text,
        ) || children;
      return (
        <CollectionNode leaf={textChild.props.text} collectionText={propsWithSource.Text}>
          {children}
        </CollectionNode>
      );
    }
    return children;
  }, [propsWithSource.Text, children]);

  return React.createElement(
    comp,
    {
      id: `cu-${id}`,
      ...attributes,
      className,
      contentEditable: !propsWithSource.Text,
      suppressContentEditableWarning: true,
      style: unpackShorthandStyles({
        ...responsiveCss,
        ...(style ?? {}),
      }),
      tabIndex: 0,
      'data-nid': collectionItemId ? `${id}-${collectionItemId}` : id,
      'data-cy': `node-${element.type ? element.type.toLowerCase() : 'rte-element'}`,
      'data-canvas': false,
      'data-collection-item-id': collectionItemId || undefined,
    },
    sanitizedChildren,
  );
});

RTENode.displayName = 'RTENode';
